import type { MouseEvent, ReactNode } from "react";
import { cn } from "../../lib/utils.js";
import { IconTile } from "../../atoms/IconTile/index.js";
import { Link } from "../../atoms/Link/index.js";
import {
  AccessOriginBadge,
  type AccessOrigin,
} from "../AccessOriginBadge/index.js";
import { Card, CardBody } from "../Card/index.js";
import {
  openCardClasses,
  openWrapperClasses,
  type ModuleLibraryCardColourProps,
} from "./module-library-card-colour.js";

export interface ModuleLibraryCardBaseProps {
  readonly title: string;
  readonly description?: string | null | undefined;
  /** Opens the item from a title button. Ignored when `href` is set. With
   *  neither `onOpen` nor `href` the card is static: no press, no ring. */
  readonly onOpen?: (() => void) | undefined;
  /** The item's address: the title is a stretched link, and it wins over
   *  `onOpen`. Without `onNavigate` the browser follows it. */
  readonly href?: string | undefined;
  /** The host's router (Breadcrumb's rule): it gets a plain primary click on
   *  the `href` title. Middle-click and modifier clicks stay the browser's. */
  readonly onNavigate?: ((href: string) => void) | undefined;
  readonly openLabel?: string | undefined;
  /** A glyph in an IconTile (sm, rounded, `tint`) at the start of the
   *  header row. */
  readonly icon?: ReactNode;
  /** Compact module-specific context such as phase, type, or health. */
  readonly headerSlot?: ReactNode;
  /** Interactive controls. This is the only slot raised above the open overlay. */
  readonly actionsSlot?: ReactNode;
  /** Optional extra facts between the description and the common footer. */
  readonly detailsSlot?: ReactNode;
  readonly footerLeadingSlot?: ReactNode;
  readonly footerTrailingSlot?: ReactNode;
  /** Created items render no marker; shared items render an explicit access badge. */
  readonly accessOrigin?: AccessOrigin | undefined;
  readonly className?: string | undefined;
}

export type ModuleLibraryCardProps = ModuleLibraryCardBaseProps &
  ModuleLibraryCardColourProps;

/**
 * Shared card skeleton for module libraries. Modules supply only their compact
 * context and metadata; title, description, footer placement, click target,
 * keyboard behaviour, spacing, and access-origin presentation stay identical.
 *
 * The title is the open target: a button (`onOpen`) or a stretched Link
 * (`href`, which wins when both are set) whose transparent `::after` covers
 * the card. The actions slot is lifted above that overlay, keeping menus
 * valid and clickable without nesting interactive controls.
 */
export function ModuleLibraryCard({
  title,
  description,
  onOpen,
  href,
  onNavigate,
  openLabel = `Open ${title}`,
  accent,
  tint,
  icon,
  headerSlot,
  actionsSlot,
  detailsSlot,
  footerLeadingSlot,
  footerTrailingSlot,
  accessOrigin,
  className,
}: ModuleLibraryCardProps): JSX.Element {
  const opens = Boolean(href || onOpen);
  const showHeader =
    icon != null ||
    headerSlot != null ||
    actionsSlot != null ||
    accessOrigin === "shared";
  const showFooter = footerLeadingSlot != null || footerTrailingSlot != null;

  const navigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate || !href || event.defaultPrevented) return;
    // Only a plain primary click is the host's; middle-click and the
    // modifier chords open a new tab or window (Breadcrumb's rule).
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    onNavigate(href);
  };

  /* `press` sits on the positioned wrapper, not on the title target: its
     `::after` is what covers the card, and a transform on it (the pressed
     dip) would make it its own containing block, shrinking the hit target
     mid-click so the mouseup lands off it. The wrapper is already the
     containing block, and `:active` matches ancestors of the pressed
     element, so the whole card dips together. */
  return (
    <div
      className={cn(
        "group relative h-full rounded-xl",
        opens && openWrapperClasses(tint, accent),
        className,
      )}
    >
      <Card className={cn("h-full", opens && openCardClasses(tint, accent))}>
        <CardBody className="flex h-full min-h-48 flex-col gap-3">
          {showHeader ? (
            <div className="flex min-h-7 items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {icon != null ? (
                  <IconTile
                    size="sm"
                    shape="rounded"
                    className="shrink-0"
                    {...(tint ? { tint } : {})}
                  >
                    {icon}
                  </IconTile>
                ) : null}
                {headerSlot}
              </div>
              {accessOrigin === "shared" || actionsSlot ? (
                <div className="relative z-10 flex shrink-0 items-center gap-2">
                  {accessOrigin ? (
                    <AccessOriginBadge origin={accessOrigin} />
                  ) : null}
                  {actionsSlot}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-snug text-pb-text">
              {href ? (
                /* The card draws the one focus indicator, as it does for the
                   button, so the Link's own ring is dropped. */
                <Link
                  href={href}
                  variant="plain"
                  display="block"
                  stretched
                  aria-label={openLabel}
                  onClick={navigate}
                  className="line-clamp-2 focus-visible:ring-0"
                >
                  {title}
                </Link>
              ) : onOpen ? (
                <button
                  type="button"
                  onClick={onOpen}
                  aria-label={openLabel}
                  className="line-clamp-2 text-start after:absolute after:inset-0 after:cursor-pointer after:content-[''] focus:outline-none"
                >
                  {title}
                </button>
              ) : (
                <span className="line-clamp-2">{title}</span>
              )}
            </h3>
            <p className="mt-1 line-clamp-2 min-h-8 text-xs leading-relaxed text-pb-text-muted">
              {description || "No description yet."}
            </p>
          </div>

          {detailsSlot ? (
            <div className="text-xs text-pb-text-secondary">{detailsSlot}</div>
          ) : null}

          {showFooter ? (
            <div className="mt-auto grid min-h-7 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-dense-1">
              {footerLeadingSlot != null ? (
                <div className="min-w-0 overflow-hidden whitespace-nowrap">
                  {footerLeadingSlot}
                </div>
              ) : null}
              {footerTrailingSlot != null ? (
                <div
                  className={cn(
                    "min-w-0 whitespace-nowrap text-end",
                    footerLeadingSlot == null ? "col-start-2" : undefined,
                  )}
                >
                  {footerTrailingSlot}
                </div>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
